import React, { useState } from 'react'
import { Button, Form } from 'react-bootstrap'
import Spinner from 'react-bootstrap/Spinner'
import { withGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { REGEX_EMAIL, errors } from '../../constants'
import { createAccount } from 'services/manage'
import { verifyOTPCode } from '../../services/auth'
import OTPInput from '../OTPInput'

import { getErrorMessage } from '../../utils/general'
import { withUtmParams } from '../../utils/utm'
import * as Sentry from '@sentry/react'
import SuccessIcon from '../SuccessIcon/SuccessIcon'

const SuccessBoxVerify = ({ googleReCaptchaProps, domain, onClose, recordedEmail = false, setRootState }) => {
  const [email, setEmail] = useState(typeof recordedEmail === 'string' ? recordedEmail : '')
  const [invalidEmail, setInvalidEmail] = useState(false)
  const [isFetching, setIsFetching] = useState(false)
  const [emailResponse, setEmailResponse] = useState({ emailSent: recordedEmail })
  const [showEmailInput, setShowEmailInput] = useState(false)
  const [otpLoading, setOtpLoading] = useState(false)
  const [otpError, setOtpError] = useState('')

  const handleVerifyOTP = async (code) => {
    if (!code || code.length < 4) return
    setOtpLoading(true)
    setOtpError('')

    const emailToVerify = email || (typeof recordedEmail === 'string' ? recordedEmail : '')

    try {
      const res = await verifyOTPCode(emailToVerify, code)
      if (res.data?.newAccount) {
        window.location = `/free-welcome?id=${res.data.setupToken}&email=${encodeURIComponent(emailToVerify.toLowerCase().trim())}&trial=true`
      } else {
        localStorage.setItem('token', res.data.token)
        sessionStorage.removeItem('hasSkippedFreeTrialB2C')
        window.location = '/manage'
      }
    } catch (err) {
      setOtpLoading(false)
      setOtpError('Invalid or expired code. Please try again.')
    }
  }

  const onKeep = async () => {
    const isValid =
      REGEX_EMAIL.test(email.toLowerCase()) && email.toLowerCase().search('mailinator') === -1 && !email.includes('+')

    if (!isValid) {
      setInvalidEmail(true)
      return
    } else {
      setInvalidEmail(false)
    }

    setIsFetching(true)

    const recaptchaValue = await googleReCaptchaProps.executeRecaptcha('homepage')
    const headers = {
      'x-recaptcha': recaptchaValue
    }

    createAccount(
      withUtmParams({
        subdomain: domain.split('.')[0],
        tld: `.${domain.split('.').slice(1).join('.')}`,
        email
      }),
      {
        headers: {
          'Content-Type': 'application/json',
          ...headers
        }
      }
    )
      .then((res) => {
        setIsFetching(false)

        if (res.data.success) {
          setEmailResponse({
            emailSent: res.data.success
          })
        }
      })
      .catch((err) => {
        setIsFetching(false)
        setEmailResponse({
          notOwner: err.response.data.code === errors.NOT_OWNER
        })

        const errorMessage = getErrorMessage(err)
        if (errorMessage && errorMessage !== '') {
          setRootState({
            errorMessage: getErrorMessage(err)
          })
        } else if (errorMessage && errorMessage === '') {
          setRootState({
            errorMessage: 'Oops, something went wrong'
          })
          console.error(err)
        } else {
          setRootState({
            errorMessage: 'Oops, something went wrong'
          })
          Sentry.captureException(err, {
            tags: {
              section: 'success-box-verify-error'
            }
          })
        }
      })
  }

  const handleResendClick = () => {
    setShowEmailInput(true)
  }

  const { emailSent, notOwner } = emailResponse

  return (
    <div className="success-box">
      <div onClick={onClose}>
        <img className="exit" src="delete.png" alt="quit" />
      </div>
      <h3>Success</h3>
      <div className="flex justify-content-center align-items-center my-4">
        <SuccessIcon width={40} height={40} />
      </div>
      <h4 className="domain">{domain || 'You link'}</h4>
      <h5>
        <>is nearly live...</>
      </h5>
      <React.Fragment>
        {isFetching ? (
          <Spinner className="mt-5" animation="border" />
        ) : (
          <div className="container-lock-site">
            {notOwner && <div>Sorry, that link name is taken</div>}
            <div>
              <div className="grey-background round-8 p-4">
                <div className="bold font-20">
                  <img alt="email" src="/assets/icons/mail.svg" height={30} className="me-2" />
                  We've sent you a verification email with a code
                </div>
                <div className="font-18">Please check your inbox or spam folder</div>
                <div className="mt-4 mb-2">
                  <OTPInput
                    onComplete={handleVerifyOTP}
                    email={email}
                    error={otpError !== ''}
                    loading={otpLoading}
                    hideFallbackText={true}
                  />
                </div>

                <Form.Group className="mb-0 mt-4 pt-3 border-top">
                  <div className="text-center text-muted mb-2 font-14">Didn't receive the code?</div>
                  <div className="flex justify-center ms-auto me-auto">
                    {showEmailInput ? (
                      <>
                        <Form.Control
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="Email"
                          aria-label="email"
                          type="email"
                        />
                        <Button className="ms-2" variant="outline-primary" onClick={onKeep}>
                          Resend
                        </Button>
                      </>
                    ) : (
                      <Button variant="outline-primary" onClick={handleResendClick}>
                        Resend email
                      </Button>
                    )}
                  </div>
                  {invalidEmail && <div className="validation-error">Sorry, that's not a valid email</div>}
                </Form.Group>
              </div>
            </div>
          </div>
        )}
      </React.Fragment>
    </div>
  )
}

export default withGoogleReCaptcha(SuccessBoxVerify)
